<template>
  <BaseArchive
    :articles="articles"
    :link="link"
    :meta="meta"
    :class="rootClass"
    class="archive-tag"
    slug="tag"
  >
    <template #top>
      <div
        v-if="isTop"
        id="title-group-cover"
        class="archive-page__cover"
      />
    </template>

    <template #header>
      <BaseTitleGroup
        :rubric="tag"
        :is-first-page="pageNumber === 1"
        :is-simple="isSimple"
      />
    </template>
  </BaseArchive>
</template>

<script lang="ts">
  import type { TArchivesResponse } from '~/types/api/archives';
  import BaseTitleGroup from '~/components/BaseComponents/BaseTitleGroup.vue';
  import BaseArchive from '~/components/BaseComponents/BaseArchive.vue';
  import { useWidgetsStore } from '~/store/widgets';
  import { useSeoStore } from '~/store/seo';
  import { useWindowStore } from '~/store/window';
  import { ARCHIVES_ARTICLES_COUNT_ON_PAGE } from '~/constants';
  import { useArchivesStore } from '~/store/archives';
  import { useHeadWithSeo } from '~/composables/useHeadWithSeo';

  export default defineNuxtComponent({
    name: 'ArchiveTag',

    components: {
      BaseArchive,
      BaseTitleGroup,
    },

    provide() {
      return {
        eventCategory: () => 'Теги',
      };
    },

    scrollToTop: true,

    async setup() {
      const widgetsStore = useWidgetsStore();
      const seoStore = useSeoStore();
      const archivesStore = useArchivesStore();
      const widgetsSidebar = widgetsStore.sidebarWidgets;
      const isDesktopAndMore = useWindowStore().isDesktopAndMore;
      const { $api } = useNuxtApp();

      const route = useRoute();
      const tagName = computed(() =>
        Array.isArray(route.params.tagName) ? route.params.tagName[0] : route.params.tagName,
      );

      if (!tagName.value) {
        throw createError({
          statusCode: 404,
          statusMessage: 'Invalid tag name',
        });
      }

      const pageNumber = computed(() =>
        Number(route.params.pageNumber) ? Number(route.params.pageNumber) : 1,
      );

      const { data, error } = await useAsyncData<TArchivesResponse>(
        `ARCHIVE_TAG_${tagName.value}_${pageNumber.value}`,
        async () => {
          const { data } = await $api.archives.tag({
            slug: tagName.value,
            page: pageNumber.value,
            limit: ARCHIVES_ARTICLES_COUNT_ON_PAGE,
          });

          return data;
        },
      );

      if (!data.value || data.value?.meta.total_pages === 0) {
        throw createError({ statusCode: 404, message: 'No data' });
      } else if (error.value) {
        throw createError(error.value);
      }

      const pagesDataRoute = route.fullPath.replace(route.hash, '');

      seoStore.addSeoByPath(pagesDataRoute, data.value.seo);
      useHeadWithSeo();

      widgetsStore.setVisibilityWidgetsCondition([
        {
          type: 'tag',
          value: data.value.item.id.toString(),
        },
      ]);

      archivesStore.setMeta(data.value.meta);

      return {
        route,
        widgetsSidebar,
        isDesktopAndMore,
        tagName,
        pageNumber,

        articles: data.value.items,
        meta: data.value.meta,
        tag: data.value.item,
      };
    },

    computed: {
      link(): HTMLAnchorElement {
        return {
          href: `/tag/${this.tagName}`,
        } as HTMLAnchorElement;
      },

      rootClass() {
        return {
          'archive-tag--simple': this.isSimple,
        };
      },

      isSimple() {
        if (this.pageNumber > 1) {
          return true;
        }

        return (
          !this.tag.color && this.tag.covers.length === 0 && !this.tag.parsed_description.length
        );
      },

      isTop() {
        return this.isDesktopAndMore && this.pageNumber === 1 && this.tag.covers.length;
      },
    },
  });
</script>

<style lang="scss" scoped>
  .archive-tag {
    :deep() {
      .title-group {
        @include tablet {
          padding: $spacing-6 $spacing-12 $spacing-0;
        }
        @include big-tablet {
          padding: $spacing-8 $spacing-14 $spacing-0;
        }
        @include desktop {
          padding: $spacing-0;
        }
      }
      .archive__pagination {
        padding: $spacing-2 12px;
        @include tablet {
          padding: $spacing-2 18px;
        }
        @include big-tablet {
          padding: $spacing-2 $spacing-6;
        }
        @include desktop {
          padding: $spacing-2 $spacing-8;
        }
      }
    }
    &__title {
      font-family: $headline-font--1;

      @include headline-small-text;
      font-weight: 400;

      @include tablet {
        font-size: 32px;
        line-height: 42px;
      }
    }
  }
</style>
